import { useCallback, useEffect, useState } from "react";
import { useMap } from "react-leaflet";
import maplibregl from "maplibre-gl";
import { Protocol } from "pmtiles";
import { type LatLngLiteral } from "leaflet";
import { useMaplibreContext } from "@/features/map/components/mapLibre/context/MaplibreLayerContext";
import { isMaplibreMapAlive } from "@/features/map/utils/leafletMaplibreInternals";
import { type MapLibreClickHandler } from "@/features/map/types/mapLibre";
import { registerPrintLayerAdapter } from "@/features/map/utils/printLayerAdapterRegistry";
import { createMaplibrePrintAdapter } from "@/features/map/utils/maplibrePrintAdapter";

import {
  INTERACTIVE_LAYER_METADATA,
  isTopmostInteractiveLayer,
} from "@/features/map/utils/maplibreClickPriority";
import {
  REGISTRATION_MAP_COVERAGE_FILL_COLOR,
  REGISTRATION_MAP_COVERAGE_FILL_OPACITY,
  REGISTRATION_MAP_PARCEL_STROKE_COLOR,
  REGISTRATION_MAP_PARCEL_STROKE_OPACITY,
} from "@/features/map/constants/registrationMapLayer";

export const REGISTRATION_MAP_LAYER_NAME = "登記所備付地図";

const SOURCE_NAME = "registration-map";
const COVERAGE_SOURCE_LAYER = "registration_coverage";
const PARCEL_SOURCE_LAYER = "registration_parcels";
const LABEL_SOURCE_LAYER = "registration_parcel_labels";
const SOURCE_MAX_ZOOM = 16;
const PARCEL_MIN_ZOOM = 14;
const LABEL_MIN_ZOOM = 17;
// 法務省の利用規約で、出典（法務省）と加工した旨の記載が求められている
export const REGISTRATION_MAP_SOURCE_URL =
  "https://front.geospatial.jp/moj-chizu-xml-readme/";
export const REGISTRATION_MAP_SOURCE_LINK_TEXT =
  "「登記所備付地図データ」（法務省）";
export const REGISTRATION_MAP_SOURCE_TEXT = `${REGISTRATION_MAP_SOURCE_LINK_TEXT}を加工して作成`;
const ATTRIBUTION = `<a href="${REGISTRATION_MAP_SOURCE_URL}">${REGISTRATION_MAP_SOURCE_LINK_TEXT}</a>を加工して作成`;

const LAYER_IDS = {
  coverageFill: "registration-map-coverage-fill",
  parcelFill: "registration-map-parcel-fill",
  parcelLine: "registration-map-parcel-line",
  label: "registration-map-label",
} as const;

const protocol = new Protocol();
let protocolRegistered = false;

const ensurePmtilesProtocol = (): void => {
  if (protocolRegistered) return;
  maplibregl.addProtocol("pmtiles", protocol.tile);
  protocolRegistered = true;
};

interface Props {
  visible: boolean;
  pmtilesUrl: string;
  onParcelClick: (latlng: LatLngLiteral) => void;
}

/** 独自整備した登記所備付地図PMTilesをMapLibreで描画する。 */
export const RegistrationMapTileLayer: React.FC<Props> = ({
  visible,
  pmtilesUrl,
  onParcelClick,
}) => {
  const leafletMap = useMap();
  const { maplibreMap } = useMaplibreContext();
  // レイヤーを追加し終えたか。スタイル未ロードでマウントすると追加が load イベント後に
  // ずれるため、表示切替・クリック登録はこれを待ってから行う。
  const [layersReady, setLayersReady] = useState(false);

  useEffect(() => {
    if (!maplibreMap || pmtilesUrl === "") return;
    return registerPrintLayerAdapter(leafletMap, () =>
      createMaplibrePrintAdapter({
        map: maplibreMap,
        layers: [
          { id: LAYER_IDS.coverageFill, visible },
          { id: LAYER_IDS.parcelFill, visible },
          {
            id: LAYER_IDS.parcelLine,
            visible,
            paintPixelProperties: ["line-width"],
          },
          {
            id: LAYER_IDS.label,
            visible,
            layoutPixelProperties: ["text-size", "text-padding"],
            paintPixelProperties: ["text-halo-width"],
          },
        ],
      })
    );
  }, [leafletMap, maplibreMap, pmtilesUrl, visible]);

  const handleParcelClick = useCallback<MapLibreClickHandler>(
    (event) => {
      if (
        !maplibreMap ||
        !isTopmostInteractiveLayer(
          maplibreMap,
          event.point,
          LAYER_IDS.parcelFill
        )
      )
        return;
      event.originalEvent.stopPropagation();
      const { lat, lng } = event.lngLat;
      onParcelClick({ lat, lng });
    },
    [maplibreMap, onParcelClick]
  );

  const setupLayers = useCallback((): void => {
    if (!maplibreMap || pmtilesUrl === "") return;

    ensurePmtilesProtocol();

    // URL更新や遅延ロードでも、地番系を既存のデータレイヤーより下に挿入する。
    const beforeId = maplibreMap
      .getStyle()
      .layers.find(
        (layer) => !Object.values(LAYER_IDS).some((id) => id === layer.id)
      )?.id;

    if (!maplibreMap.getSource(SOURCE_NAME)) {
      maplibreMap.addSource(SOURCE_NAME, {
        type: "vector",
        url: `pmtiles://${pmtilesUrl}`,
        minzoom: 0,
        maxzoom: SOURCE_MAX_ZOOM,
        promoteId: {
          [PARCEL_SOURCE_LAYER]: "rm_id",
          [LABEL_SOURCE_LAYER]: "rm_id",
        },
      });
    }

    if (!maplibreMap.getLayer(LAYER_IDS.coverageFill)) {
      maplibreMap.addLayer(
        {
          id: LAYER_IDS.coverageFill,
          type: "fill",
          source: SOURCE_NAME,
          "source-layer": COVERAGE_SOURCE_LAYER,
          minzoom: 0,
          maxzoom: PARCEL_MIN_ZOOM,
          layout: { visibility: "none" },
          paint: {
            "fill-color": REGISTRATION_MAP_COVERAGE_FILL_COLOR,
            "fill-opacity": REGISTRATION_MAP_COVERAGE_FILL_OPACITY,
          },
        },
        beforeId
      );
    }

    if (!maplibreMap.getLayer(LAYER_IDS.parcelFill)) {
      maplibreMap.addLayer(
        {
          id: LAYER_IDS.parcelFill,
          metadata: INTERACTIVE_LAYER_METADATA,
          type: "fill",
          source: SOURCE_NAME,
          "source-layer": PARCEL_SOURCE_LAYER,
          minzoom: PARCEL_MIN_ZOOM,
          layout: { visibility: "none" },
          paint: {
            "fill-color": "#2474d2",
            "fill-opacity": 0,
          },
        },
        beforeId
      );
    }

    if (!maplibreMap.getLayer(LAYER_IDS.parcelLine)) {
      maplibreMap.addLayer(
        {
          id: LAYER_IDS.parcelLine,
          type: "line",
          source: SOURCE_NAME,
          "source-layer": PARCEL_SOURCE_LAYER,
          minzoom: PARCEL_MIN_ZOOM,
          layout: { visibility: "none" },
          paint: {
            "line-color": REGISTRATION_MAP_PARCEL_STROKE_COLOR,
            "line-opacity": REGISTRATION_MAP_PARCEL_STROKE_OPACITY,
            "line-width": 1,
          },
        },
        beforeId
      );
    }

    if (!maplibreMap.getLayer(LAYER_IDS.label)) {
      maplibreMap.addLayer(
        {
          id: LAYER_IDS.label,
          type: "symbol",
          source: SOURCE_NAME,
          "source-layer": LABEL_SOURCE_LAYER,
          minzoom: LABEL_MIN_ZOOM,
          layout: {
            visibility: "none",
            "text-field": ["get", "chiban"],
            "text-size": 13,
            "text-padding": 2,
            "text-allow-overlap": false,
          },
          paint: {
            "text-color": "#123b6d",
            "text-halo-color": "#ffffff",
            "text-halo-width": 2,
          },
        },
        beforeId
      );
    }

    setLayersReady(true);
  }, [maplibreMap, pmtilesUrl]);

  useEffect(() => {
    if (!maplibreMap || pmtilesUrl === "") return;

    const handleReady = (): void => {
      maplibreMap.off("load", handleReady);
      maplibreMap.off("idle", handleReady);
      setupLayers();
    };
    if (maplibreMap.isStyleLoaded()) {
      setupLayers();
    } else {
      // load は地図の初回ロードで一度しか発火しない。初回ロード後に URL が届き、
      // 他ソースのタイル読込中で isStyleLoaded() が false の場合は idle で拾う。
      maplibreMap.on("load", handleReady);
      maplibreMap.on("idle", handleReady);
    }

    return () => {
      maplibreMap.off("load", handleReady);
      maplibreMap.off("idle", handleReady);
      setLayersReady(false);
      if (isMaplibreMapAlive(maplibreMap)) {
        Object.values(LAYER_IDS)
          .reverse()
          .forEach((layerId) => {
            if (maplibreMap.getLayer(layerId)) {
              maplibreMap.removeLayer(layerId);
            }
          });
        if (maplibreMap.getSource(SOURCE_NAME)) {
          maplibreMap.removeSource(SOURCE_NAME);
        }
      }
    };
  }, [maplibreMap, pmtilesUrl, setupLayers]);

  useEffect(() => {
    if (!maplibreMap || !layersReady) return;
    const visibility = visible ? "visible" : "none";
    Object.values(LAYER_IDS).forEach((layerId) => {
      if (maplibreMap.getLayer(layerId)) {
        maplibreMap.setLayoutProperty(layerId, "visibility", visibility);
      }
    });
    if (!visible) return;

    // 出典はレイヤーを表示している間だけ出す
    leafletMap.attributionControl.addAttribution(ATTRIBUTION);
    return () => {
      leafletMap.attributionControl.removeAttribution(ATTRIBUTION);
    };
  }, [leafletMap, maplibreMap, pmtilesUrl, visible, layersReady]);

  useEffect(() => {
    if (!maplibreMap || !visible || !layersReady) return;
    maplibreMap.on("click", LAYER_IDS.parcelFill, handleParcelClick);

    return () => {
      if (isMaplibreMapAlive(maplibreMap)) {
        maplibreMap.off("click", LAYER_IDS.parcelFill, handleParcelClick);
      }
    };
  }, [handleParcelClick, maplibreMap, visible, layersReady]);

  return null;
};
